import { CardModalListType, IMultiCardModal, paddingTopClassMap, paddingBottomClassMap } from '@/common/pd-meta-components-types'
import { FC, useMemo, useRef, useState } from 'react'
import SlidingTabContent from '../../sliding-tab-content'
import clsx from 'clsx'
import PdImage from '@/components/pd-image/pd-image'
import SlidingTabs from '../../pd-sliding-tabs'
import { Modal } from 'antd'

const Card: FC<Partial<CardModalListType> & { borderRadius?: number }> = ({
  title,
  desc,
  mediaUrl,
  textColor,
  modalTitle,
  modalDesc,
  modalMediaGroup,
  borderRadius,
}) => {
  const cardRef = useRef<HTMLDivElement>(null)
  const [modalVisible, setModalVisible] = useState<boolean>(false)

  return (
    <div className={clsx('relative select-none', 'w-72 h-384px', 'laptop:w-72 laptop:h-384px')}>
      <div ref={cardRef} className={clsx('w-full h-full relative [transform-style:preserve-3d]')}>
        {/* 正面 */}
        <div
          className={clsx('w-full h-full absolute overflow-hidden [backface-visibility:hidden]', 'rounded-2xl')}
          style={{
            borderRadius: `${borderRadius}px`,
          }}
        >
          <div
            className={clsx('absolute top-0 left-0 z-10', 'w-full p-16px', 'whitespace-pre-wrap text-xl font-semibold')}
            style={{ color: textColor }}
          >
            <h1 className="text-fc-primary">{title}</h1>
            <p className="text-fc-secondary pd-sub-text mt-10px">{desc}</p>
          </div>
          <PdImage src={mediaUrl || ''} enableMask={false} object="cover" width={'100%'} height={'100%'} />
          <span
            className={clsx(
              'absolute rounded-3xl cursor-pointer transition',
              "bg-[url('/icons/icon_white_add.svg')] bg-no-repeat bg-black/60 backdrop-blur-lg bg-center",
              'w-8 h-8 bottom-16px right-16px'
            )}
            onClick={() => setModalVisible(true)}
          />
        </div>
      </div>
      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        destroyOnClose
        className="!w-180 top-10"
        bodyStyle={{
          minHeight: '90vh',
          borderRadius: `${borderRadius}px`,
        }}
        maskStyle={{
          background: 'rgba(0, 0, 0, 0.40)',
          backdropFilter: 'blur(24px)',
        }}
        footer={null}
        closeIcon={
          <span
            className={clsx(
              'fixed rounded-3xl cursor-pointer transition',
              "bg-[url('/icons/icon_white_add.svg')] bg-no-repeat bg-black/60 backdrop-blur-lg bg-center",
              'w-8 h-8 left-[calc(50%+23rem)] top-[calc(50%-1rem)]',
              'rotate-45'
            )}
          />
        }
      >
        {modalTitle && <h1 className="text-fc-primary pd-second-title mb-2 font-semibold">{modalTitle}</h1>}
        {modalDesc && <p className="text-fc-primary pd-sub-text mb-10">{modalDesc}</p>}
        <ul className="flex flex-col gap-10">
          {modalMediaGroup?.map((item) => (
            <li key={item.title}>
              {item.mediaUrl && (
                <div className="w-full h-75 mb-4">
                  <PdImage src={item.mediaUrl || ''} enableMask={false} object="cover" width={'100%'} height={'100%'} />
                </div>
              )}
              <h2 className="text-fc-primary pd-main-text mb-2 font-semibold">{item.title}</h2>
              <p className="text-fc-primary pd-sub-text">{item.desc}</p>
            </li>
          ))}
        </ul>
      </Modal>
    </div>
  )
}

const MultiCardModal: FC<IMultiCardModal> = ({ dataList = [], theme, borderRadius, paddingTop = 'px100', paddingBottom = 'px100' }) => {
  const [activeTabKey, setActiveTabKey] = useState<number>(0)

  const data = useMemo(() => {
    return dataList[activeTabKey] || {}
  }, [dataList, activeTabKey])

  const handleActiveTabChange = (activeIndex: number) => {
    setActiveTabKey(activeIndex)
  }

  return (
    <div className={clsx('w-full overflow-hidden', `pd-theme-${theme} bg-container-primary`)}>
      <div
        className={clsx(
          'mx-auto overflow-visible w-full laptop:max-w-300',
          'h-fit box-border laptop:px-0',
          paddingTopClassMap[paddingTop],
          paddingBottomClassMap[paddingBottom]
        )}
      >
        <SlidingTabs
          childrenBoxClassName={clsx('px-6', 'laptop:px-0')}
          contentBoxClassName={clsx('h-444px box-border laptop:h-504px')}
          tabsBoxClassName={clsx('pb-32px laptop:pb-40px')}
          tabsContainerClassName="laptop:rounded-full"
          tabLabels={dataList.map((item) => item.tabTitle || '')}
          activeTabChange={handleActiveTabChange}
        >
          {dataList.map((item, index) => (
            <SlidingTabContent data={item.cardList || []} key={index} navigationBoxClassName="absolute bottom-0">
              <Card borderRadius={borderRadius} />
            </SlidingTabContent>
          ))}
        </SlidingTabs>

        <div className={clsx('justify-between align-top', 'mt-32px px-6', 'laptop:flex laptop:mt-24px laptop:px-0')}>
          <div className={clsx('flex-1')}>
            <div className={clsx('font-semibold text-fc-primary', 'text-24', 'laptop:text-32 laptop:leading-40px')}>
              {data?.bottomTitle}
            </div>
            <div
              className={clsx(
                'font-normal',
                'text-fc-secondary text-16 mt-12px',
                'laptop:text-fc-primary laptop:text-xl laptop:mt-8px'
              )}
            >
              {data?.bottomSubTitle}
            </div>
            <a
              className="block w-fit text-brand-primary hover:text-brand-hover active:text-brand-active text-base font-normal mt-12px laptop:mt-8px"
              href={data?.downloadUrl || ''}
            >
              {data?.downloadTitle}
            </a>
          </div>

          {!!data.bottomDesc && (
            <div
              className={clsx(
                'flex-1 flex flex-col',
                'font-normal',
                'mt-12px text-fc-secondary text-16',
                'laptop:mt-0 laptop:text-fc-primary laptop:text-sm'
              )}
            >
              {data.bottomDesc}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MultiCardModal
